import { useRef, useState } from 'react';
import { Loader2, ThumbsUp, ThumbsDown } from 'lucide-react';
import { submitIncidentFeedback } from '../../services/incidents';
import { cn } from '../../lib/utils';

interface InlineFeedbackProps {
  tenantId: string;
  incidentId: string;
  feedbackType: 'summary' | 'mitre' | 'risk' | 'actions';
}

type FeedbackState = 'idle' | 'submitting' | 'up' | 'down' | 'error';

export function InlineFeedback({ tenantId, incidentId, feedbackType }: InlineFeedbackProps) {
  const [state, setState] = useState<FeedbackState>('idle');
  const idempotencyKey = useRef(crypto.randomUUID());

  const handleFeedback = async (positive: boolean) => {
    if (state === 'submitting' || state === 'up' || state === 'down') return;

    const rating = positive ? 5 : 1;
    setState('submitting');
    try {
      const response = await submitIncidentFeedback(tenantId, incidentId, {
        feedbackType,
        rating,
        source: 'inline',
        idempotencyKey: idempotencyKey.current,
      });
      if (!response.success) throw new Error(response.error || 'Feedback was not saved');
      setState(positive ? 'up' : 'down');
    } catch (error) {
      console.error('Inline feedback failed:', error);
      setState('error');
    }
  };

  if (state === 'up' || state === 'down') {
    return (
      <div className="flex items-center gap-1">
        {state === 'up' ? (
          <ThumbsUp className="w-3.5 h-3.5 text-green-500 fill-green-500" />
        ) : (
          <ThumbsDown className="w-3.5 h-3.5 text-red-400 fill-red-400" />
        )}
        <span className="text-xs text-text-muted">Thanks</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-0.5">
      {state === 'error' && (
        <span className="mr-1 text-xs text-red-500" role="alert">Not saved—retry</span>
      )}
      <button
        type="button"
        onClick={() => handleFeedback(true)}
        className={cn(
          'p-1 rounded transition-colors',
          'text-text-muted hover:text-green-500 hover:bg-accent-green/10'
        )}
        title="AI output was helpful"
        disabled={state === 'submitting'}
      >
        {state === 'submitting' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ThumbsUp className="w-3.5 h-3.5" />}
      </button>
      <button
        type="button"
        onClick={() => handleFeedback(false)}
        className={cn(
          'p-1 rounded transition-colors',
          'text-text-muted hover:text-red-400 hover:bg-accent-red/10'
        )}
        title="AI output needs improvement"
        disabled={state === 'submitting'}
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
